import type { trpc } from './trpc';

type ItemQueryUtils = ReturnType<typeof trpc.useUtils>;

// Operations and the itinerary Planner render the SAME itinerary items through
// different routers (`operations.list` vs `itineraries.byId`) over one shared
// QueryClient. Any item/subitem mutation from either side must refresh both so
// edits are instantly visible in the other without a manual refresh.
//
// Invalidation is argless (partial-match) so it hits every input variant — the
// planner reads `byId` with `{ id, includeSubitems: true }` and operations
// reads `list` with a filter object; a keyless invalidate matches them all.
export const invalidateItemQueries = (utils: ItemQueryUtils): void => {
  // Operations side.
  utils.operations.list.invalidate();
  utils.operations.filterCounts.invalidate();
  // Metric cards (batch) + toolbar bullets — same data as list filters.
  utils.operations.countMany.invalidate();
  utils.operations.flaggedCounts.invalidate();
  utils.operations.failedMessageCount.invalidate();
  // Ops Dashboard side. Its Red Flag card counts the same red flags, so clearing
  // one has to move that number too — otherwise the card keeps quoting a total
  // its own CTA no longer matches (XT-625).
  utils.operationsDashboard.redFlags.invalidate();
  // Planner side.
  utils.itineraries.byId.invalidate();
  utils.itineraries.search.invalidate();
  // A price edit can stop an item following its dynamic pricing (XT-951).
  utils.itineraryItems.dynamicPricing.invalidate();
  // Incident side. A red-flag note IS an incident report, so saving or clearing
  // one files/retires an incident server-side — which moves the Incidents
  // toolbar badge (`myUnresolvedCount`) and both incident lists. Without this
  // the badge only caught up on a reload.
  utils.incidents.myUnresolvedCount.invalidate();
  utils.incidents.myList.invalidate();
  utils.incidents.list.invalidate();
};
